/**
 * Settings (docs/GAME_DESIGN.md § 8): language, sound, how to answer (with
 * handwriting practice and help), accessibility, privacy, purchases behind the
 * parental gate, the Watch preview, and resetting progress.
 *
 * The same panel opens as a sheet over a paused run (context "in-game"); there
 * it leaves out everything that doesn't belong mid-run (store, privacy, Watch,
 * reset).
 */
import { useId, useState } from 'react';
import type { AudioSettings, Entitlements, HandwritingRecognizer, InputPreference, PlayerProfile, PlusFeatures, Settings } from '../contracts.ts';
import type { SettingsPatch } from '../state/settings.ts';
import type { MockPurchaseService } from '../services/purchases.ts';
import { useT } from '../i18n/index.ts';
import { CalibrationPanel } from '../input/CalibrationPanel.tsx';
import { Button, IconButton } from '../components/Button.tsx';
import { ConfirmDialog } from '../components/ConfirmDialog.tsx';
import { ChoiceGroup, Slider, Toggle } from '../components/Controls.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { Icon } from '../components/Icon.tsx';
import { useParentalGate } from '../components/ParentalGate.tsx';
import { ScreenLayout, Section } from '../components/ScreenLayout.tsx';
import { StoreSheet, formatDate } from '../components/StoreSheet.tsx';
import './settings.css';

export interface SettingsPanelProps {
  context: 'menu' | 'in-game';
  settings: Settings;
  onChange(patch: SettingsPatch): void;
  recognizer: HandwritingRecognizer;
  profile: PlayerProfile | null;
  /** Save the leaderboard opt-in on the profile. Resolves false when it couldn't be saved. */
  onSaveOptIn(optIn: boolean): Promise<boolean>;
  /** Open onboarding (for players without a leaderboard profile yet). */
  onJoin(): void;
  purchases: MockPurchaseService;
  entitlements: Entitlements;
  /** Which Plus extras exist (the store marks the rest "coming soon"). */
  plusFeatures: PlusFeatures;
  /** Watch preview currently showing (setting or ?watch=1). */
  watchActive: boolean;
  onWatchPreview(on: boolean): void;
  onResetProgress(): Promise<void>;
}

export interface SettingsScreenProps extends Omit<SettingsPanelProps, 'context'> {
  onBack(): void;
}

/** Settings as a full screen (from the Start or Game-over screen). */
export function SettingsScreen({ onBack, ...panel }: SettingsScreenProps) {
  const t = useT();
  return (
    <ScreenLayout title={t('shell.setTitle')} onBack={onBack} backLabel={t('common.back')} className="settings">
      <SettingsPanel context="menu" {...panel} />
    </ScreenLayout>
  );
}

export interface InGameSettingsProps extends Omit<SettingsPanelProps, 'context'> {
  onClose(): void;
}

/**
 * Settings as a sheet over the paused game. The sheet is long, so besides
 * "Done" at the bottom it has a close button in its header.
 */
export function InGameSettings({ onClose, ...panel }: InGameSettingsProps) {
  const t = useT();
  const titleId = useId();
  return (
    <Dialog onClose={onClose} labelledBy={titleId} variant="sheet" className="settings settings--sheet">
      <div className="ui-dialog__head">
        <Icon name="settings" size="1.6em" />
        <h2 id={titleId} className="ui-dialog__title">
          {t('shell.setTitle')}
        </h2>
        <IconButton icon="close" variant="ghost" label={t('common.close')} className="settings__close" onClick={onClose} />
      </div>
      <SettingsPanel context="in-game" {...panel} />
      <div className="ui-dialog__actions">
        <Button variant="primary" icon="check" onClick={onClose}>
          {t('common.done')}
        </Button>
      </div>
    </Dialog>
  );
}

function SettingsPanel(props: SettingsPanelProps) {
  const { context, settings, onChange } = props;
  const t = useT();
  const menu = context === 'menu';

  return (
    <div className="settings__sections">
      <Section title={t('shell.setLanguage')} icon={<Icon name="globe" />}>
        <ChoiceGroup
          legend={t('shell.setLanguage')}
          hideLegend
          value={settings.locale}
          onChange={(locale) => onChange({ locale })}
          choices={[
            { value: 'en', label: 'English', lang: 'en' },
            { value: 'af', label: 'Afrikaans', lang: 'af' },
          ]}
        />
      </Section>

      <SoundSection audio={settings.audio} onChange={(audio) => onChange({ audio })} />

      <AnsweringSection {...props} />

      <Section title={t('shell.setAccessibility')} icon={<Icon name="eye" />}>
        <Toggle
          label={t('shell.setLargeText')}
          description={t('shell.setLargeTextDesc')}
          checked={settings.largeText}
          onChange={(largeText) => onChange({ largeText })}
        />
        <Toggle
          label={t('shell.setReducedMotion')}
          description={t('shell.setReducedMotionDesc')}
          checked={settings.reducedMotion}
          onChange={(reducedMotion) => onChange({ reducedMotion })}
        />
        <Toggle
          label={t('shell.setHighContrast')}
          description={t('shell.setHighContrastDesc')}
          checked={settings.highContrast}
          onChange={(highContrast) => onChange({ highContrast })}
        />
        <Toggle
          label={t('shell.setLeftHanded')}
          description={t('shell.setLeftHandedDesc')}
          checked={settings.leftHanded}
          onChange={(leftHanded) => onChange({ leftHanded })}
        />
        <Toggle
          label={t('shell.setRelaxedPace')}
          description={t('shell.setRelaxedPaceDesc')}
          checked={settings.relaxedPace}
          onChange={(relaxedPace) => onChange({ relaxedPace })}
        />
      </Section>

      {menu && <PrivacySection {...props} />}
      {menu && <PurchasesSection {...props} />}

      {menu && (
        <Section title={t('shell.setWatch')} icon={<Icon name="watch" />}>
          <Toggle
            label={t('shell.setWatchToggle')}
            description={t('shell.setWatchDesc')}
            checked={props.watchActive}
            onChange={props.onWatchPreview}
          />
        </Section>
      )}

      {menu && <ResetSection onReset={props.onResetProgress} />}
    </div>
  );
}

function SoundSection({ audio, onChange }: { audio: AudioSettings; onChange(patch: Partial<AudioSettings>): void }) {
  const t = useT();
  return (
    <Section title={t('shell.setSound')} icon={<Icon name={audio.muted ? 'mute' : 'sound'} />}>
      <Toggle label={t('shell.setMute')} description={t('shell.setMuteDesc')} checked={audio.muted} onChange={(muted) => onChange({ muted })} />
      <div className="settings__sliders">
        <Slider label={t('shell.setVolumeMaster')} value={audio.master} disabled={audio.muted} onChange={(master) => onChange({ master })} />
        <Slider label={t('shell.setVolumeMusic')} value={audio.music} disabled={audio.muted} onChange={(music) => onChange({ music })} />
        <Slider label={t('shell.setVolumeEffects')} value={audio.sfx} disabled={audio.muted} onChange={(sfx) => onChange({ sfx })} />
      </div>
    </Section>
  );
}

function AnsweringSection({ settings, onChange, recognizer }: SettingsPanelProps) {
  const t = useT();
  const [calibrating, setCalibrating] = useState(false);
  const samples = recognizer.userSampleCount();

  /** Persist whether the recogniser now holds personal samples (Settings.handwritingCalibrated). */
  const finishCalibration = (calibrated: boolean) => {
    onChange({ handwritingCalibrated: calibrated });
    setCalibrating(false);
  };

  return (
    <Section title={t('shell.setInput')} icon={<Icon name="pencil" />}>
      <ChoiceGroup<InputPreference>
        legend={t('shell.setInput')}
        hideLegend
        variant="cards"
        value={settings.input}
        onChange={(input) => onChange({ input })}
        choices={[
          { value: 'auto', label: t('shell.setInputAuto'), description: t('shell.setInputAutoDesc') },
          { value: 'mc', label: t('shell.setInputChoices'), description: t('shell.setInputChoicesDesc') },
          { value: 'hw', label: t('shell.setInputHandwriting'), description: t('shell.setInputHandwritingDesc') },
        ]}
      />
      <div className="settings__row">
        <p className="settings__note">
          {samples > 0 ? t('shell.setCalibrated', { count: samples }) : t('shell.setNotCalibrated')}
        </p>
        <Button icon="hand" onClick={() => setCalibrating(true)}>
          {t('shell.setCalibrate')}
        </Button>
      </div>
      <details className="settings__help">
        <summary>{t('shell.setHelpTitle')}</summary>
        <ul>
          <li>{t('shell.setHelpWrite')}</li>
          <li>{t('shell.setHelpFix')}</li>
          <li>{t('shell.setHelpChoices')}</li>
          <li>{t('shell.setHelpKeyboard')}</li>
          <li>{t('shell.setHelpPrivate')}</li>
        </ul>
      </details>
      {calibrating && (
        <Dialog
          label={t('shell.setCalibrate')}
          // Closed from outside the panel (Escape, the close button): still record what it learned.
          onClose={() => finishCalibration(recognizer.userSampleCount() > 0)}
          className="settings__calibration"
        >
          <CalibrationPanel recognizer={recognizer} onDone={finishCalibration} />
        </Dialog>
      )}
    </Section>
  );
}

function PrivacySection({ profile, onSaveOptIn, onJoin }: SettingsPanelProps) {
  const t = useT();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const onboarded = Boolean(profile?.onboarded);

  const change = async (optIn: boolean) => {
    setSaving(true);
    setFailed(false);
    const ok = await onSaveOptIn(optIn);
    setSaving(false);
    setFailed(!ok);
  };

  return (
    <Section title={t('shell.setPrivacy')} icon={<Icon name="lock" />}>
      <p className="settings__text">{t('shell.setPrivacyPublic')}</p>
      <ul className="settings__list">
        <li>{t('shell.setPrivacyItemName')}</li>
        <li>{t('shell.setPrivacyItemSchool')}</li>
        <li>{t('shell.setPrivacyItemScores')}</li>
      </ul>
      <p className="settings__text">{t('shell.setPrivacyNever')}</p>
      {onboarded ? (
        <Toggle
          label={t('shell.setOptIn')}
          description={t('shell.setOptInDesc')}
          checked={profile?.leaderboardOptIn ?? false}
          disabled={saving}
          onChange={(optIn) => void change(optIn)}
        />
      ) : (
        <div className="settings__row">
          <p className="settings__note">{t('shell.setOptInNeedsProfile')}</p>
          <Button icon="trophy" onClick={onJoin}>
            {t('shell.goJoinButton')}
          </Button>
        </div>
      )}
      <p className="settings__status" role="status">
        {failed ? t('shell.setOptInFailed') : ''}
      </p>
    </Section>
  );
}

function PurchasesSection({ purchases, entitlements, plusFeatures }: SettingsPanelProps) {
  const t = useT();
  const { requestGate, gate } = useParentalGate();
  const [storeOpen, setStoreOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreKey, setRestoreKey] = useState<string | null>(null);

  const openStore = async () => {
    if (await requestGate()) setStoreOpen(true);
  };

  const restore = async () => {
    if (!(await requestGate())) return;
    setRestoring(true);
    setRestoreKey(null);
    try {
      const e = await purchases.restore();
      setRestoreKey(e.adsRemoved || e.plus ? 'shell.setRestored' : 'shell.setRestoreNothing');
    } catch {
      setRestoreKey('shell.storeFailed');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Section title={t('shell.setPurchases')} icon={<Icon name="bag" />}>
      <p className="settings__text">{entitlementText(entitlements, t)}</p>
      <div className="settings__buttons">
        <Button variant="primary" icon="sparkle" onClick={() => void openStore()}>
          {t('shell.setStore')}
        </Button>
        <Button icon="refresh" busy={restoring} onClick={() => void restore()}>
          {t('shell.setRestore')}
        </Button>
      </div>
      <p className="settings__note">{t('shell.setGateNote')}</p>
      <p className="settings__status" role="status">
        {restoreKey ? t(restoreKey) : ''}
      </p>
      {storeOpen && (
        <StoreSheet
          purchases={purchases}
          entitlements={entitlements}
          testMode={purchases.testMode}
          plusFeatures={plusFeatures}
          requestGate={requestGate}
          onClose={() => setStoreOpen(false)}
        />
      )}
      {gate}
    </Section>
  );
}

function entitlementText(e: Entitlements, t: ReturnType<typeof useT>): string {
  if (e.plus) return e.plusExpiresAt ? t('shell.setPlusUntil', { date: formatDate(e.plusExpiresAt) }) : t('shell.setPlusActive');
  if (e.adsRemoved) return t('shell.setAdsRemoved');
  return t('shell.setNoPurchases');
}

function ResetSection({ onReset }: { onReset(): Promise<void> }) {
  const t = useT();
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  return (
    <Section title={t('shell.setReset')} icon={<Icon name="trash" />}>
      <p className="settings__text">{t('shell.setResetDesc')}</p>
      <Button variant="danger" icon="trash" onClick={() => setConfirming(true)}>
        {t('shell.setResetButton')}
      </Button>
      <p className="settings__status" role="status">
        {done ? t('shell.setResetDone') : ''}
      </p>
      {confirming && (
        <ConfirmDialog
          title={t('shell.setResetConfirmTitle')}
          body={t('shell.setResetConfirmBody')}
          confirmLabel={t('shell.setResetButton')}
          cancelLabel={t('common.cancel')}
          danger
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            void onReset().then(() => setDone(true));
          }}
        />
      )}
    </Section>
  );
}
